<template>
  <div>
    <ValidationObserver ref="form" v-slot="{ invalid }">
      <v-overlay :value="loading" absolute>
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>

      <v-card outlined>
        <v-card-title class="text-h6">CRM 通知設定</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <ValidationProvider
                v-slot="{ errors }"
                name="續保提醒天數"
                rules="required|numeric|min_value:1"
              >
                <v-text-field
                  v-model.number="config.renewalReminderDays"
                  :error="!!errors.length"
                  :error-messages="errors"
                  :disabled="saving"
                  type="number"
                  flat
                  label="續保提醒天數 *"
                  outlined
                  dense
                  hide-details="auto"
                  hint="在到期日前多少天發送續保提醒"
                  persistent-hint
                />
              </ValidationProvider>
            </v-col>

            <v-col cols="12" md="6">
              <div>
                <v-switch
                  v-model="config.autoReminderEnabled"
                  :disabled="saving"
                  label="啟用自動提醒"
                  color="primary"
                  hide-details
                  dense
                  class="mt-0"
                >
                  <template v-slot:label>
                    <span class="text-body-2">啟用自動提醒</span>
                  </template>
                </v-switch>
              </div>
              <div class="text-caption grey--text mt-1">於每天9:00AM自動發送續保提醒通知給客戶</div>
            </v-col>
          </v-row>
          <v-row v-if="debug">
            <v-col cols="12" md="6">
              <ValidationProvider v-slot="{ errors }" name="WhatsApp 測試號碼">
                <v-text-field
                  v-model="config.whatsappDebugNumber"
                  :error="!!errors.length"
                  :error-messages="errors"
                  :disabled="saving"
                  flat
                  label="WhatsApp 測試號碼"
                  trim
                  outlined
                  dense
                  hide-details="auto"
                  hint="格式: +852xxxxxxxx , 留空為發送給真實的 WhatsApp 號碼"
                  persistent-hint
                  placeholder="+85200000000"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="12" md="6">
              <v-switch
                v-model="config.isSendWhatsapp"
                :disabled="saving"
                label="發送 WhatsApp 訊息"
                color="primary"
                hide-details
                dense
                class="mt-0"
              >
                <template v-slot:label>
                  <span class="text-body-2">發送 WhatsApp 訊息</span>
                </template>
              </v-switch>
              <div class="text-caption grey--text mt-1">通過 WhatsApp 發送通知訊息</div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn :disabled="invalid || saving" :loading="saving" color="primary" @click="save">
            <v-icon left small>mdi-content-save</v-icon>
            儲存設定
          </v-btn>
          <v-btn :disabled="saving" color="default" outlined @click="resetForm">
            <v-icon left small>mdi-refresh</v-icon>
            重置
          </v-btn>
        </v-card-actions>
      </v-card>
    </ValidationObserver>
  </div>
</template>

<script>
import api from '@/api';
import ApiError from '@/api/ApiError';
import { mapGetters } from 'vuex';

export default {
  name: 'CRMNotificationSetting',
  data() {
    return {
      config: {
        renewalReminderDays: 15,
        autoReminderEnabled: true,
        isSendWhatsapp: false,
        whatsappDebugNumber: '',
      },
      loading: false,
      saving: false,
    };
  },
  mounted() {
    this.loadConfig();
  },
  computed: {
    ...mapGetters(['debug']),
  },
  methods: {
    async loadConfig() {
      this.loading = true;
      try {
        const response = await api.get('/v1/system-config/module/crm');
        if (response.data) {
          this.config = {
            renewalReminderDays: response.data.renewalReminderDays || 15,
            autoReminderEnabled:
              response.data.autoReminderEnabled !== undefined
                ? response.data.autoReminderEnabled
                : true,
            isSendWhatsapp:
              response.data.isSendWhatsapp !== undefined ? response.data.isSendWhatsapp : false,
            whatsappDebugNumber: response.data.whatsappDebugNumber || '',
          };
        }
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message || '載入設定失敗',
          type: 'error',
          color: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        if (!(await this.$refs.form?.validate?.())) {
          this.saving = false;
          return;
        }

        const payload = {
          renewalReminderDays: Number(this.config.renewalReminderDays),
          autoReminderEnabled: this.config.autoReminderEnabled,
          isSendWhatsapp: this.config.isSendWhatsapp,
          whatsappDebugNumber: this.config.whatsappDebugNumber,
        };

        await api.patch('/v1/system-config/module/crm', payload);

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '設定儲存成功',
          type: 'success',
          color: 'success',
        });

        // Reload to ensure data is in sync
        await this.loadConfig();
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message || '儲存設定失敗',
          type: 'error',
          color: 'error',
        });
      } finally {
        this.saving = false;
      }
    },
    async resetForm() {
      const confirmed = await this.$root.$confirm.open(
        '確認重置',
        '確定要重置CRM通知設定嗎？此操作無法復原。',
        {
          color: 'red',
        },
        '確認重置',
        '取消',
      );
      if (!confirmed) {
        return;
      }
      this.$refs.form?.reset();
      this.loadConfig();
    },
  },
};
</script>

<style scoped>
/* Custom styles */
</style>
